import React, { useContext, useEffect, useMemo } from "react";
import { Tooltip, message } from "antd";
import type { Rule } from "antd/es/form";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";

import { Button, Form, Input, Select } from "../../shared";
import AppContext from "../../../context/AppContext";
import { SIDER_TABS } from "../../../utils/Constants";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { formatString } from "../../../utils/constants/stringUtils";
import type { RootState } from "../../../store";
import { usePageTitle } from "../../CommonComponents/Layout/PageTitleContext";

import styles from "./Limits.module.css";

type PaymentsAppContext = {
  setActiveSidebarTab: (tab: string, option?: string) => void;
};

type LimitsFormValues = {
  minDepositAmount: string;
  maxDepositAmount: string;
  depositCurrency: string;
  minWithdrawalAmount: string;
  maxWithdrawalAmount: string;
  withdrawalCurrency: string;
  maxDailyWithdrawals: string;
};

type AmountFieldProps = {
  amountName: keyof LimitsFormValues;
  label: string;
  amountTestId: string;
  /** Shared currency for the min/max pair — only rendered on one field of the pair. */
  currencyName?: keyof LimitsFormValues;
  currencyOptions?: { value: string; label: string }[];
  currencyTestId?: string;
  /** Re-run amount rules when these sibling fields change (e.g. min → max). */
  amountDependencies?: (keyof LimitsFormValues)[];
  extraAmountRules?: Rule[];
};

/**
 * Ensures a max amount is ≥ its paired min. `0` means "no limit" on either
 * field, so those values skip the comparison. Currency is shared per pair
 * (`depositCurrency` / `withdrawalCurrency`), so amounts are always comparable.
 */
const maxAtLeastMin =
  (minFieldName: keyof LimitsFormValues, errorMessage: string): Rule =>
  ({ getFieldValue }) => ({
    validator(_, value) {
      if (value === undefined || value === null || value === "") {
        return Promise.resolve();
      }
      const minRaw = getFieldValue(minFieldName);
      if (minRaw === undefined || minRaw === null || minRaw === "") {
        return Promise.resolve();
      }
      const max = Number(value);
      const min = Number(minRaw);
      if (Number.isNaN(max) || Number.isNaN(min)) {
        return Promise.resolve();
      }
      if (max === 0 || min === 0 || max >= min) {
        return Promise.resolve();
      }
      return Promise.reject(new Error(errorMessage));
    },
  });

function LabelWithInfo({ label }: { label: string }) {
  return (
    <span className={styles.labelWithTooltip}>
      {label}
      <Tooltip
        title={AppStrings.PAYMENTS_LIMITS_ZERO_MEANS_NO_LIMIT}
        trigger={["hover", "click"]}
      >
        <InfoCircleOutlined
          className={styles.infoIcon}
          aria-label={AppStrings.PAYMENTS_LIMITS_ZERO_MEANS_NO_LIMIT}
        />
      </Tooltip>
    </span>
  );
}

function AmountField({
  amountName,
  label,
  amountTestId,
  currencyName,
  currencyOptions,
  currencyTestId,
  amountDependencies,
  extraAmountRules = [],
}: AmountFieldProps) {
  const showCurrency = Boolean(currencyName && currencyOptions && currencyTestId);

  return (
    <Form.Item
      label={<LabelWithInfo label={label} />}
      required={false}
    >
      <div className={styles.amountControl}>
        <Form.Item
          name={amountName}
          noStyle
          dependencies={amountDependencies}
          rules={[
            {
              required: true,
              message: formatString(AppStrings.FIELD_IS_REQUIRED, {
                title: label,
              }),
            },
            Form.validators.numericRange({ min: 0 }),
            ...extraAmountRules,
          ]}
        >
          <Input
            type="number"
            min={0}
            className={
              showCurrency ? styles.amountInput : styles.amountInputSolo
            }
            data-testid={amountTestId}
          />
        </Form.Item>
        {showCurrency && (
          <Form.Item
            name={currencyName}
            noStyle
            rules={[
              {
                required: true,
                message: formatString(AppStrings.FIELD_IS_REQUIRED, {
                  title: AppStrings.CURRENCY,
                }),
              },
            ]}
          >
            <Select
              className={styles.currencySelect}
              options={currencyOptions}
              data-testid={currencyTestId}
            />
          </Form.Item>
        )}
      </div>
    </Form.Item>
  );
}

const Limits: React.FC = () => {
  usePageTitle(AppStrings.LIMITS);

  const { setActiveSidebarTab } = useContext(
    AppContext,
  ) as PaymentsAppContext;
  const [form] = Form.useForm<LimitsFormValues>();

  const appCurrencyConfig = useSelector(
    (state: RootState) => state.config.appConfig?.app_currency_config,
  );

  const currencyOptions = useMemo(() => {
    const primary =
      appCurrencyConfig?.primary_currency?.shortcode?.trim() || "USD";
    const secondary = appCurrencyConfig?.secondary_currency?.shortcode?.trim();
    const options = [{ value: primary, label: primary }];
    if (secondary && secondary !== primary) {
      options.push({ value: secondary, label: secondary });
    }
    return options;
  }, [appCurrencyConfig]);

  const defaultCurrency = currencyOptions[0]?.value ?? "USD";

  useEffect(() => {
    setActiveSidebarTab(SIDER_TABS.PAYMENTS, "LIMITS");
  }, [setActiveSidebarTab]);

  const handleFinish = (_values: LimitsFormValues) => {
    // WIP — wire to payments limits API when the endpoint ships.
    message.success(AppStrings.PAYMENTS_LIMITS_SAVE_SUCCESS);
  };

  return (
    <>
      <div className={styles.page}>
        <Form<LimitsFormValues>
          form={form}
          className={styles.form}
          data-testid="payments-limits-form"
          onFinish={handleFinish}
          initialValues={{
            minDepositAmount: "1",
            maxDepositAmount: "1000",
            depositCurrency: defaultCurrency,
            minWithdrawalAmount: "1",
            maxWithdrawalAmount: "1000",
            withdrawalCurrency: defaultCurrency,
            maxDailyWithdrawals: "1",
          }}
        >
          <div className={styles.row}>
            <AmountField
              amountName="minDepositAmount"
              label={AppStrings.MINIMUM_DEPOSIT_AMOUNT}
              amountTestId="payments-limits-min-deposit-amount"
              currencyName="depositCurrency"
              currencyOptions={currencyOptions}
              currencyTestId="payments-limits-deposit-currency"
            />
            <AmountField
              amountName="maxDepositAmount"
              label={AppStrings.MAXIMUM_DEPOSIT_AMOUNT}
              amountTestId="payments-limits-max-deposit-amount"
              amountDependencies={["minDepositAmount"]}
              extraAmountRules={[
                maxAtLeastMin(
                  "minDepositAmount",
                  AppStrings.PAYMENTS_LIMITS_MAX_MUST_BE_GTE_MIN,
                ),
              ]}
            />
          </div>

          <div className={styles.row}>
            <AmountField
              amountName="minWithdrawalAmount"
              label={AppStrings.MINIMUM_WITHDRAWAL_AMOUNT}
              amountTestId="payments-limits-min-withdrawal-amount"
              currencyName="withdrawalCurrency"
              currencyOptions={currencyOptions}
              currencyTestId="payments-limits-withdrawal-currency"
            />
            <AmountField
              amountName="maxWithdrawalAmount"
              label={AppStrings.MAXIMUM_WITHDRAWAL_AMOUNT}
              amountTestId="payments-limits-max-withdrawal-amount"
              amountDependencies={["minWithdrawalAmount"]}
              extraAmountRules={[
                maxAtLeastMin(
                  "minWithdrawalAmount",
                  AppStrings.PAYMENTS_LIMITS_MAX_MUST_BE_GTE_MIN,
                ),
              ]}
            />
          </div>

          <div className={styles.row}>
            <Form.Item
              name="maxDailyWithdrawals"
              label={
                <LabelWithInfo label={AppStrings.MAXIMUM_DAILY_WITHDRAWALS} />
              }
              rules={[
                {
                  required: true,
                  message: formatString(AppStrings.FIELD_IS_REQUIRED, {
                    title: AppStrings.MAXIMUM_DAILY_WITHDRAWALS,
                  }),
                },
                Form.validators.numericRange({ min: 0, integer: true }),
              ]}
            >
              <Input
                type="number"
                min={0}
                step={1}
                data-testid="payments-limits-max-daily-withdrawals"
              />
            </Form.Item>
          </div>

          <div className={styles.footer}>
            <Button
              htmlType="submit"
              intent="primary"
              data-testid="payments-limits-save"
            >
              {AppStrings.SAVE}
            </Button>
          </div>
        </Form>
      </div>
    </>
  );
};

export default Limits;
